import React from 'react';

// D-07 §20 — 공용 아이콘. 색은 컨테이너가 currentColor 로 준다(§20.4).
// 원본: Figma DS PT8wG7aRcWg4CerPViHOI4 — site 2867-2226 / photo 2316-264 / error 2867-2251.
// viewBox 는 48×48 고정. 크기를 바꿀 땐 호출부에서 width/height 를 덮는다(size prop 없음).

const base = {
  width: 48,
  height: 48,
  viewBox: '0 0 48 48',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 3,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
} as const;

type IconProps = React.SVGProps<SVGSVGElement>;

/** 위치·현장 (맵 핀) — EmptyState 기본 아이콘 */
export function IconSite(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <path d="M8 20.286C8 11.292 15.164 4 24 4C32.836 4 40 11.292 40 20.286C40 29.21 34.894 39.626 26.926 43.348C26.0102 43.7768 25.0113 43.9991 24 43.9991C22.9887 43.9991 21.9898 43.7768 21.074 43.348C13.106 39.624 8 29.212 8 20.288V20.286Z" />
      <path d="M24 26C27.3137 26 30 23.3137 30 20C30 16.6863 27.3137 14 24 14C20.6863 14 18 16.6863 18 20C18 23.3137 20.6863 26 24 26Z" />
    </svg>
  );
}

/** 사진 (카메라) */
export function IconPhoto(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <path d="M29 8H19L14 14H8C6.93913 14 5.92172 14.4214 5.17157 15.1716C4.42143 15.9217 4 16.9391 4 18V36C4 37.0609 4.42143 38.0783 5.17157 38.8284C5.92172 39.5786 6.93913 40 8 40H40C41.0609 40 42.0783 39.5786 42.8284 38.8284C43.5786 38.0783 44 37.0609 44 36V18C44 16.9391 43.5786 15.9217 42.8284 15.1716C42.0783 14.4214 41.0609 14 40 14H34L29 8Z" />
      <path d="M24 32C27.3137 32 30 29.3137 30 26C30 22.6863 27.3137 20 24 20C20.6863 20 18 22.6863 18 26C18 29.3137 20.6863 32 24 32Z" />
    </svg>
  );
}

/** 에러 (방패 + 느낌표) — ErrorState 기본 아이콘 */
export function IconError(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <path d="M6 20.834C6 14.438 6 11.24 6.756 10.164C7.51 9.09 10.516 8.06 16.53 6.002L17.676 5.61C20.81 4.536 22.376 4 24 4C25.624 4 27.19 4.536 30.324 5.61L31.47 6.002C37.484 8.06 40.49 9.09 41.244 10.164C42 11.24 42 14.44 42 20.834V23.982C42 35.258 33.522 40.732 28.202 43.054C26.76 43.684 26.04 44 24 44C21.96 44 21.24 43.684 19.798 43.054C14.478 40.73 6 35.26 6 23.982V20.834Z" />
      <path d="M24 16V24" />
      {/* 느낌표 점은 fill 도형 — 루트의 stroke 를 상속하면 외곽선이 생기므로 끈다(D-07 §20.4) */}
      <path
        d="M24 32C25.1046 32 26 31.1046 26 30C26 28.8954 25.1046 28 24 28C22.8954 28 22 28.8954 22 30C22 31.1046 22.8954 32 24 32Z"
        fill="currentColor"
        stroke="none"
      />
    </svg>
  );
}

// ── 상태 아이콘 (채움 원형) ───────────────────────────────────────────────
// Figma DS PT8wG7aRcWg4CerPViHOI4 — complete 3989-3389 / warning 3989-3385 /
// cancel 3989-3383 / information 3989-3387. 네 개 모두 24px 채움 원 + 글리프 녹아웃이다.
// 위 3종(선형·48 viewBox)과 달리 stroke 가 없다 — 원을 채우고 글리프를 반대 방향으로
// 그려 파내는 방식이라 path 하나로 끝난다(마스크 불필요).
// 색은 컨테이너가 currentColor 로 준다(D-07 §20.4) — `.alert-<kind> .ai` 나
// 호출부의 text-* 유틸이 status 토큰을 지정한다.

const solid = {
  width: 24,
  height: 24,
  viewBox: '0 0 24 24',
  fill: 'currentColor',
  'aria-hidden': true,
} as const;

// 선형 인라인 — 상태 아이콘이 아닌 컨트롤용(닫기 등)이 계속 쓴다. viewBox 24 · 기본 18px.
const inline = {
  width: 18,
  height: 18,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
} as const;

/** 안내·정보 (원 + i) */
export function IconInfoCircle(props: IconProps) {
  return (
    <svg {...solid} {...props}>
      <path d="M12 2C17.523 2 22 6.477 22 12C22 17.523 17.523 22 12 22C6.477 22 2 17.523 2 12C2 6.477 6.477 2 12 2ZM11 10C10.7348 10 10.4804 10.1054 10.2929 10.2929C10.1054 10.4804 10 10.7348 10 11C10 11.2652 10.1054 11.5196 10.2929 11.7071C10.4804 11.8946 10.7348 12 11 12V17C11 17.2652 11.1054 17.5196 11.2929 17.7071C11.4804 17.8946 11.7348 18 12 18H12.5C12.7198 17.9996 12.9334 17.9268 13.1076 17.7929C13.2819 17.6589 13.4072 17.4713 13.4641 17.259C13.521 17.0466 13.5064 16.8215 13.4225 16.6183C13.3386 16.4152 13.1901 16.2453 13 16.135V11C13 10.7348 12.8946 10.4804 12.7071 10.2929C12.5196 10.1054 12.2652 10 12 10H11ZM12 7C11.7348 7 11.4804 7.10536 11.2929 7.29289C11.1054 7.48043 11 7.73478 11 8C11 8.26522 11.1054 8.51957 11.2929 8.70711C11.4804 8.89464 11.7348 9 12 9H12.002C12.2672 9 12.5216 8.89464 12.7091 8.70711C12.8966 8.51957 13.002 8.26522 13.002 8C13.002 7.73478 12.8966 7.48043 12.7091 7.29289C12.5216 7.10536 12.2672 7 12.002 7H12Z" />
    </svg>
  );
}

/** 완료·성공 (채움 원 + 체크) */
export function IconCheckCircle(props: IconProps) {
  return (
    <svg {...solid} {...props}>
      <path d="M12 2C17.523 2 22 6.477 22 12C22 17.523 17.523 22 12 22C6.477 22 2 17.523 2 12C2 6.477 6.477 2 12 2ZM16.95 8.38C16.8571 8.28702 16.7468 8.21326 16.6254 8.16294C16.504 8.11262 16.3739 8.08671 16.2425 8.08671C16.1111 8.08671 15.981 8.11262 15.8596 8.16294C15.7382 8.21326 15.6279 8.28702 15.535 8.38L10.585 13.331L8.464 11.21C8.37175 11.1145 8.26141 11.0383 8.1394 10.9859C8.0174 10.9335 7.88618 10.9059 7.7534 10.9047C7.62062 10.9036 7.48894 10.9289 7.36605 10.9792C7.24315 11.0295 7.1315 11.1037 7.0376 11.1976C6.94371 11.2915 6.86946 11.4031 6.81918 11.526C6.7689 11.6489 6.7436 11.7806 6.74475 11.9134C6.7459 12.0462 6.77349 12.1774 6.8259 12.2994C6.87831 12.4214 6.95449 12.5318 7.05 12.624L9.879 15.452C10.0665 15.6395 10.3208 15.7448 10.586 15.7448C10.8512 15.7448 11.1055 15.6395 11.293 15.452L16.949 9.795C17.1365 9.60747 17.2418 9.35316 17.2418 9.088C17.2418 8.82284 17.1365 8.56853 16.949 8.381Z" />
    </svg>
  );
}

/** 가이드·도움말 (원 + 물음표) — 사용 가이드 EmptyState 기본 아이콘 (D-09 §15.37).
 *  화면들이 인라인 SVG 로 복제하던 16px 물음표를 48 viewBox 로 옮긴 것이다. */
export function IconGuide(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <circle cx="24" cy="24" r="20" />
      <path d="M18.18 18a6 6 0 0 1 11.66 2c0 4-6 4-6 8" />
      <path d="M24 35v.02" />
    </svg>
  );
}

/** 경고·주의 (채움 원 + 느낌표). 시안 개편 전에는 삼각형이었고 이름도 IconWarningTriangle 이었다. */
export function IconWarningCircle(props: IconProps) {
  return (
    <svg {...solid} {...props}>
      <path d="M12 2C17.523 2 22 6.477 22 12C22 17.523 17.523 22 12 22C6.477 22 2 17.523 2 12C2 6.477 6.477 2 12 2ZM12 15C11.7348 15 11.4804 15.1054 11.2929 15.2929C11.1054 15.4804 11 15.7348 11 16C11 16.2652 11.1054 16.5196 11.2929 16.7071C11.4804 16.8946 11.7348 17 12 17H12.002C12.2672 17 12.5216 16.8946 12.7091 16.7071C12.8966 16.5196 13.002 16.2652 13.002 16C13.002 15.7348 12.8966 15.4804 12.7091 15.2929C12.5216 15.1054 12.2672 15 12.002 15H12ZM12 6C11.7348 6 11.4804 6.10536 11.2929 6.29289C11.1054 6.48043 11 6.73478 11 7V13C11 13.2652 11.1054 13.5196 11.2929 13.7071C11.4804 13.8946 11.7348 14 12 14C12.2652 14 12.5196 13.8946 12.7071 13.7071C12.8946 13.5196 13 13.2652 13 13V7C13 6.73478 12.8946 6.48043 12.7071 6.29289C12.5196 6.10536 12.2652 6 12 6Z" />
    </svg>
  );
}

/** 오류·실패 (채움 원 + X) */
export function IconErrorCircle(props: IconProps) {
  return (
    <svg {...solid} {...props}>
      <path d="M12 2C17.523 2 22 6.477 22 12C22 17.523 17.523 22 12 22C6.477 22 2 17.523 2 12C2 6.477 6.477 2 12 2ZM15.535 8.465C15.3475 8.27753 15.0932 8.17221 14.828 8.17221C14.5628 8.17221 14.3085 8.27753 14.121 8.465L12 10.585L9.879 8.465C9.78675 8.36949 9.67641 8.29331 9.5544 8.2409C9.4324 8.18849 9.30118 8.1609 9.1684 8.15975C9.03562 8.1586 8.90394 8.1839 8.78105 8.23418C8.65815 8.28446 8.5465 8.35871 8.45261 8.45261C8.35871 8.5465 8.28446 8.65815 8.23418 8.78105C8.1839 8.90394 8.1586 9.03562 8.15975 9.1684C8.1609 9.30118 8.18849 9.4324 8.2409 9.5544C8.29331 9.67641 8.36949 9.78675 8.465 9.879L10.585 12L8.465 14.121C8.36949 14.2132 8.29331 14.3236 8.2409 14.4456C8.18849 14.5676 8.1609 14.6988 8.15975 14.8316C8.1586 14.9644 8.1839 15.0961 8.23418 15.219C8.28446 15.3419 8.35871 15.4535 8.45261 15.5474C8.5465 15.6413 8.65815 15.7155 8.78105 15.7658C8.90394 15.8161 9.03562 15.8414 9.1684 15.8403C9.30118 15.8391 9.4324 15.8115 9.5544 15.7591C9.67641 15.7067 9.78675 15.6305 9.879 15.535L12 13.415L14.121 15.535C14.3096 15.7172 14.5622 15.818 14.8244 15.8157C15.0866 15.8134 15.3374 15.7082 15.5228 15.5228C15.7082 15.3374 15.8134 15.0866 15.8157 14.8244C15.818 14.5622 15.7172 14.3096 15.535 14.121L13.415 12L15.535 9.879C15.7225 9.69147 15.8278 9.43716 15.8278 9.172C15.8278 8.90684 15.7225 8.65253 15.535 8.465Z" />
    </svg>
  );
}

/** 닫기 (X) — Alert `onClose` 등 컨트롤용 */
export function IconClose(props: IconProps) {
  return (
    <svg {...inline} {...props} strokeWidth={props.strokeWidth ?? 2.4}>
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

// ── 공유 팝업용 아이콘 3종 (2026-10-22 신규 승인: IconCopy · IconKakaoTalk · IconShare) ──────
// §20.1 크기 md 20 기본(호출부에서 width/height 로 16·24·32 지정), §20.2 stroke 2, §20.3 radius 금지(모서리는 직선 path), §20.4 currentColor.
const std = {
  width: 20,
  height: 20,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
} as const;

/** 복사 (겹친 두 장) */
export function IconCopy(props: IconProps) {
  return (
    <svg {...std} {...props}>
      <path d="M9 9H20V20H9Z" />
      <path d="M15 9V4H4V15H9" />
    </svg>
  );
}

/** 공유 (노드 3개 연결) */
export function IconShare(props: IconProps) {
  return (
    <svg {...std} {...props}>
      <circle cx="18" cy="5" r="3" />
      <circle cx="6" cy="12" r="3" />
      <circle cx="18" cy="19" r="3" />
      <path d="M8.6 10.5L15.4 6.5" />
      <path d="M8.6 13.5L15.4 17.5" />
    </svg>
  );
}

/** 카카오톡 (말풍선 심볼) — 브랜드 로고라 §20.2 stroke 2 규격의 예외다: 채움(fill) 도형, stroke 없음.
 *  색은 currentColor — 카카오 브랜드 색(--brand-kakao-symbol)은 컨테이너(AppTile tone="kakao")가 준다. */
export function IconKakaoTalk(props: IconProps) {
  return (
    <svg {...std} fill="currentColor" stroke="none" {...props}>
      <path d="M12 3C6.477 3 2 6.58 2 11C2 13.86 3.87 16.37 6.68 17.78L5.73 21.28C5.65 21.58 5.99 21.82 6.25 21.64L10.4 18.86C10.92 18.92 11.45 18.96 12 18.96C17.523 18.96 22 15.38 22 10.96C22 6.54 17.523 3 12 3Z" />
    </svg>
  );
}
